import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import Backend from "i18next-http-backend";
import config from "@/config";
import { registerI18nFormatters } from "@/config/i18n/formatters";
import {
  DEFAULT_NAMESPACE,
  getBundledTranslationPath,
  getCdnTranslationPath,
  getTranslationLocales,
} from "./translation-cdn";
import { fetchBundledLocalesList } from "./bundled-locales-list";
import { PRIMARY_LANGUAGE, isPrimaryLanguage } from "./primary-language";
import {
  createMissingKeyPostProcessor,
  handleFailedLoading,
  handleMissingKey,
} from "./missing-key-telemetry";
import { createMachineTranslationIndicatorPostProcessor } from "./machine-translation-indicator";
import { REACT_I18N_BINDINGS } from "./react-binding";
import {
  handleMachineTranslationLanguageChanged,
  handleMachineTranslationResourcesLoaded,
} from "./machine-translation-overlay";

function getTranslationPath(locale: string, namespace: string): string {
  if (isPrimaryLanguage(locale) || !config.translations.cdnEnabled) {
    return getBundledTranslationPath(locale, namespace);
  }

  const cdnPath = getCdnTranslationPath(locale, namespace);
  if (cdnPath !== null) {
    return cdnPath;
  }

  return getBundledTranslationPath(locale, namespace);
}

async function getSupportedLngs(): Promise<string[]> {
  if (config.translations.cdnEnabled) {
    return getTranslationLocales();
  }

  return fetchBundledLocalesList();
}

const supportedLngs = await getSupportedLngs();

await i18n
  .use(Backend)
  .use(initReactI18next)
  .use(createMissingKeyPostProcessor(i18n))
  .use(createMachineTranslationIndicatorPostProcessor(i18n))
  .init({
    supportedLngs,
    lng: PRIMARY_LANGUAGE,
    ns: DEFAULT_NAMESPACE,
    defaultNS: DEFAULT_NAMESPACE,
    fallbackLng: PRIMARY_LANGUAGE,
    debug: import.meta.env.VITE_APP_MODE === "local",
    saveMissing: true,
    missingKeyHandler: handleMissingKey,
    postProcess: ["missing-key-reporter", "machine-translation-indicator"],
    postProcessPassResolved: true,
    react: REACT_I18N_BINDINGS,
    interpolation: {
      escapeValue: false,
    },
    backend: {
      loadPath: (languages: string[], namespaces: string[]) => {
        const locale = languages[0] ?? PRIMARY_LANGUAGE;
        const namespace = namespaces[0] ?? DEFAULT_NAMESPACE;
        return getTranslationPath(locale, namespace);
      },
      requestOptions: {
        cache: "no-store",
      },
    },
  });

i18n.on("failedLoading", handleFailedLoading);
i18n.on("loaded", (loaded) => {
  handleMachineTranslationResourcesLoaded(i18n, loaded);
});
i18n.on("languageChanged", () => {
  handleMachineTranslationLanguageChanged(i18n);
});

registerI18nFormatters(i18n);

export default i18n;
